import type { CSSProperties, HTMLAttributes } from 'react';
import './styles/avatar.css';

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(' ');
}

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;

const PRESET_SIZES: Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', number> = {
  xs: 20,
  sm: 24,
  md: 28,
  lg: 40,
  xl: 56,
};

export function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export type AvatarProps = {
  name: string;
  /** Preset size or explicit pixel diameter. Default md (28px). */
  size?: AvatarSize;
  /** Square corners for org/provider marks. */
  square?: boolean;
  src?: string;
  alt?: string;
} & Omit<HTMLAttributes<HTMLSpanElement>, 'children'>;

/** Initials avatar — shared across Business Web, Admin, and shell chrome. */
export function Avatar({ name, size = 'md', square, src, alt, className, style, ...rest }: AvatarProps) {
  const px = typeof size === 'number' ? size : PRESET_SIZES[size];
  const label = alt ?? name;

  return (
    <span
      className={cx('cev-avatar', square && 'cev-avatar--square', className)}
      style={
        {
          ...style,
          '--cev-avatar-size': `${px}px`,
        } as CSSProperties
      }
      role={label ? 'img' : undefined}
      aria-label={label || undefined}
      {...rest}
    >
      {src ? (
        <img className="cev-avatar__img" src={src} alt={label} />
      ) : (
        <span className="cev-avatar__initials" aria-hidden={Boolean(label)}>
          {initialsFromName(name)}
        </span>
      )}
    </span>
  );
}
